import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Card,
    CardContent,
    Grid,
    Chip,
    Avatar,
    List,
    ListItem,
    ListItemAvatar,
    ListItemText,
    Paper,
    Divider,
    Alert,
    CircularProgress,
    Tab,
    Tabs
} from '@mui/material';
import {
    Medication,
    Person,
    AccessTime,
    CalendarToday,
    NotificationsActive,
    CheckCircle
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import useAxiosPrivate from '../hooks/useAxiosPrivate';
import { getImageUrl } from '../utils/imageUtils';

const MedicinesPage = () => {
    const { t } = useTranslation('global');
    const axiosPrivate = useAxiosPrivate();
    
    const [therapies, setTherapies] = useState([]);
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState(0);
    const [currentTime, setCurrentTime] = useState(new Date());

    // Orari delle somministrazioni (indici del campo hiring_time)
    const timeSlots = [
        { index: 0, name: 'Colazione', time: '08:00', icon: '🌅' },
        { index: 1, name: 'Metà Mattina', time: '10:00', icon: '☕' },
        { index: 2, name: 'Pranzo', time: '12:30', icon: '🍽️' },
        { index: 3, name: 'Pomeriggio', time: '15:00', icon: '🍵' },
        { index: 4, name: 'Cena', time: '19:00', icon: '🌙' },
        { index: 5, name: 'Sera', time: '21:00', icon: '🌃' },
        { index: 6, name: 'Notte', time: '23:00', icon: '🌑' },
        { index: 7, name: 'Al Bisogno', time: 'PRN', icon: '⚕️' }
    ];

    useEffect(() => {
        fetchTherapies();
        // Aggiorna l'ora corrente ogni minuto
        const interval = setInterval(() => {
            setCurrentTime(new Date());
        }, 60000);
        return () => clearInterval(interval);
    }, []);

    const fetchTherapies = async () => {
        try {
            setLoading(true);
            const response = await axiosPrivate.get('/therapy');
            console.log('🔍 Terapie ricevute:', response.data);
            setTherapies(response.data || []);
        } catch (error) {
            console.error('❌ Errore caricamento terapie:', error);
        } finally {
            setLoading(false);
        }
    };

    // Parsing hiring_time (es. "T;F;T;F;T;F;F;F")
    const parseHiringTime = (hiringTimeString) => {
        return hiringTimeString.split(';').map(slot => slot === 'T');
    };

    // Filtra terapie attive (non sospese)
    const activeTherapies = therapies.filter(t => !t.is_suspended);

    // Raggruppa per anziano
    const groupByElderly = (therapiesList) => {
        const grouped = {};
        therapiesList.forEach(therapy => {
            const elderlyId = therapy.elderly_id;
            if (!grouped[elderlyId]) {
                grouped[elderlyId] = {
                    elderly: therapy.elderly_data || {},
                    medicines: []
                };
            }
            grouped[elderlyId].medicines.push(therapy);
        });
        return Object.values(grouped);
    };

    // Ottieni ora corrente (indice dello slot)
    const getCurrentTimeSlot = () => {
        const hour = currentTime.getHours();
        if (hour >= 7 && hour < 9) return 0; // Colazione
        if (hour >= 9 && hour < 11) return 1; // Metà mattina
        if (hour >= 11 && hour < 14) return 2; // Pranzo
        if (hour >= 14 && hour < 17) return 3; // Pomeriggio
        if (hour >= 17 && hour < 20) return 4; // Cena
        if (hour >= 20 && hour < 22) return 5; // Sera
        if (hour >= 22 || hour < 7) return 6; // Notte
        return -1;
    };

    // Filtra medicine per ora corrente
    const getMedicinesForCurrentTime = () => {
        const currentSlot = getCurrentTimeSlot();
        if (currentSlot === -1) return [];
        
        return activeTherapies.filter(therapy => {
            const slots = parseHiringTime(therapy.hiring_time);
            return slots[currentSlot];
        });
    };

    // Filtra medicine della giornata
    const getMedicinesForToday = () => {
        const filtered = activeTherapies.filter(therapy => {
            const slots = parseHiringTime(therapy.hiring_time);
            // Escludi solo "Al Bisogno" (indice 7)
            const hasScheduledTime = slots.slice(0, 7).some(slot => slot);
            console.log(`📊 ${therapy.name_medicine}: slots=${therapy.hiring_time}, hasScheduled=${hasScheduledTime}`);
            return hasScheduledTime;
        });
        console.log(`✅ Terapie totali: ${therapies.length}, Attive: ${activeTherapies.length}, Giornata: ${filtered.length}`);
        return filtered;
    };

    const renderMedicineCard = (elderlyGroup, isCurrentTime = false) => {
        const { elderly, medicines } = elderlyGroup;
        
        return (
            <Card
                key={elderly.elderly_id}
                sx={{
                    mb: 2,
                    border: isCurrentTime ? '2px solid' : '1px solid',
                    borderColor: isCurrentTime 
                        ? (theme) => theme.palette.warning.main 
                        : (theme) => theme.palette.divider,
                    boxShadow: isCurrentTime ? 3 : 1,
                    transition: 'all 0.3s',
                    '&:hover': {
                        boxShadow: 4,
                        transform: 'translateY(-2px)'
                    }
                }}
            >
                <CardContent>
                    {/* Header con info anziano */}
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                        <Avatar
                            src={elderly.avatar ? getImageUrl(elderly.avatar) : undefined}
                            sx={{
                                width: 56,
                                height: 56,
                                mr: 2,
                                bgcolor: (theme) => theme.palette.primary.main
                            }}
                        >
                            {!elderly.avatar && <Person />}
                        </Avatar>
                        <Box sx={{ flexGrow: 1 }}>
                            <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                {elderly.name} {elderly.surname}
                            </Typography>
                        </Box>
                        {isCurrentTime && (
                            <Chip
                                icon={<NotificationsActive />}
                                label="ORA"
                                color="warning"
                                sx={{ fontWeight: 600 }}
                            />
                        )}
                    </Box>

                    <Divider sx={{ mb: 2 }} />

                    {/* Lista medicine */}
                    <List disablePadding>
                        {medicines.map((medicine, index) => {
                            const slots = parseHiringTime(medicine.hiring_time);
                            const activeTimes = timeSlots
                                .filter((slot, idx) => slots[idx])
                                .map(slot => slot.name);

                            return (
                                <React.Fragment key={medicine.therapy_id}>
                                    {index > 0 && <Divider sx={{ my: 1 }} />}
                                    <ListItem
                                        disablePadding
                                        sx={{
                                            py: 1,
                                            px: 0,
                                            display: 'flex',
                                            flexDirection: 'column',
                                            alignItems: 'flex-start'
                                        }}
                                    >
                                        <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', mb: 1 }}>
                                            <Medication
                                                sx={{
                                                    mr: 1,
                                                    color: (theme) => theme.palette.primary.main,
                                                    fontSize: 28
                                                }}
                                            />
                                            <Typography
                                                variant="body1"
                                                sx={{ fontWeight: 600, flexGrow: 1 }}
                                            >
                                                {medicine.name_medicine}
                                            </Typography>
                                            {medicine.kind_therapy && (
                                                <Chip
                                                    label={medicine.kind_therapy}
                                                    size="small"
                                                    variant="outlined"
                                                    color="primary"
                                                />
                                            )}
                                        </Box>
                                        
                                        {/* Orari di somministrazione */}
                                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, ml: 4 }}>
                                            {activeTimes.map((timeName, idx) => {
                                                const timeSlot = timeSlots.find(s => s.name === timeName);
                                                return (
                                                    <Chip
                                                        key={idx}
                                                        icon={<AccessTime />}
                                                        label={`${timeSlot.icon} ${timeName} (${timeSlot.time})`}
                                                        size="small"
                                                        sx={{
                                                            bgcolor: (theme) => theme.palette.action.hover,
                                                            fontWeight: 500
                                                        }}
                                                    />
                                                );
                                            })}
                                        </Box>
                                    </ListItem>
                                </React.Fragment>
                            );
                        })}
                    </List>
                </CardContent>
            </Card>
        );
    };

    const renderContent = () => {
        if (loading) {
            return (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                    <CircularProgress />
                </Box>
            );
        }

        if (activeTab === 0) {
            // Medicine ORA CORRENTE
            const currentMedicines = getMedicinesForCurrentTime();
            const groupedCurrent = groupByElderly(currentMedicines);

            if (groupedCurrent.length === 0) {
                const currentSlot = getCurrentTimeSlot();
                const currentSlotName = currentSlot >= 0 ? timeSlots[currentSlot].name : 'questo momento';
                
                return (
                    <Alert severity="info" icon={<CheckCircle />}>
                        <Typography variant="body1">
                            Nessuna medicina da somministrare in {currentSlotName.toLowerCase()}
                        </Typography>
                    </Alert>
                );
            }

            return (
                <Box>
                    <Alert severity="warning" icon={<NotificationsActive />} sx={{ mb: 3 }}>
                        <Typography variant="body1" sx={{ fontWeight: 600 }}>
                            {groupedCurrent.length} {groupedCurrent.length === 1 ? 'paziente' : 'pazienti'} in 
                            attesa di somministrazione
                        </Typography>
                    </Alert>
                    {groupedCurrent.map(group => renderMedicineCard(group, true))}
                </Box>
            );
        }

        if (activeTab === 1) {
            // Medicine GIORNATA
            const todayMedicines = getMedicinesForToday();
            const groupedToday = groupByElderly(todayMedicines);

            if (groupedToday.length === 0) {
                return (
                    <Alert severity="info">
                        <Typography variant="body1">
                            Nessuna terapia programmata per oggi
                        </Typography>
                    </Alert>
                );
            }

            return (
                <Box>
                    <Alert severity="info" icon={<CalendarToday />} sx={{ mb: 3 }}>
                        <Typography variant="body1">
                            {groupedToday.length} {groupedToday.length === 1 ? 'paziente' : 'pazienti'} con 
                            terapie programmate oggi
                        </Typography>
                    </Alert>
                    {groupedToday.map(group => renderMedicineCard(group, false))}
                </Box>
            );
        }
    };

    return (
        <Box sx={{ p: 3 }}>
            {/* Header */}
            <Box sx={{ mb: 3 }}>
                <Typography variant="h4" sx={{ fontWeight: 600, mb: 1 }}>
                    💊 Gestione Medicine
                </Typography>
                <Typography variant="body1" color="text.secondary">
                    Monitora e gestisci le somministrazioni farmacologiche
                </Typography>
            </Box>

            {/* Tabs */}
            <Paper sx={{ mb: 3 }}>
                <Tabs
                    value={activeTab}
                    onChange={(e, newValue) => setActiveTab(newValue)}
                    variant="fullWidth"
                    sx={{
                        borderBottom: 1,
                        borderColor: 'divider'
                    }}
                >
                    <Tab
                        icon={<NotificationsActive />}
                        label="Ora Corrente"
                        iconPosition="start"
                        sx={{ fontWeight: 600 }}
                    />
                    <Tab
                        icon={<CalendarToday />}
                        label="Giornata"
                        iconPosition="start"
                        sx={{ fontWeight: 600 }}
                    />
                </Tabs>
            </Paper>

            {/* Contenuto */}
            {renderContent()}
        </Box>
    );
};

export default MedicinesPage;
